import React, { useId } from 'react';
import type { PipeElbow } from './pipePorts';

export interface PipeSvgProps {
  pathData: string;
  elbows?: readonly PipeElbow[] | undefined;
  isFlowing?: boolean | undefined;
  liquidColor?: string | undefined;
  pipeColor?: string | undefined;
  diameter?: number | undefined;
  isSelected?: boolean | undefined;
  isHovered?: boolean | undefined;
  isDraft?: boolean | undefined;
  onClick?: ((e: React.MouseEvent) => void) | undefined;
  onDelete?: (() => void) | undefined;
}

export const PipeSvg: React.FC<PipeSvgProps> = ({
  pathData,
  elbows = [],
  isFlowing = false,
  liquidColor = '#0284c7', // Default fresh water blue
  pipeColor = '#475569',   // Industrial metallic steel
  diameter = 8,
  isSelected = false,
  isHovered = false,
  isDraft = false,
  onClick,
  onDelete,
}) => {
  const uid = useId().replace(/:/g, '_');
  if (!pathData) return null;

  const collarSpan = diameter * 0.65 + 1.8;

  return (
    <g
      className={`pipe-group-${uid}`}
      onClick={onClick}
      style={{ cursor: isDraft ? 'crosshair' : 'pointer' }}
    >
      <defs>
        {/* Pipe Metallic Gradient */}
        <linearGradient id={`pipeGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#94a3b8" />
          <stop offset="30%" stopColor="#cbd5e1" />
          <stop offset="65%" stopColor={pipeColor} />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        {/* Elbow Fitting Body Gradient */}
        <linearGradient id={`elbowGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#cbd5e1" />
          <stop offset="25%" stopColor="#f1f5f9" />
          <stop offset="55%" stopColor={pipeColor} />
          <stop offset="85%" stopColor="#334155" />
          <stop offset="100%" stopColor="#0f172a" />
        </linearGradient>

        {/* Weld Collar Ring Gradient */}
        <linearGradient id={`collarGrad-${uid}`} x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="#f8fafc" />
          <stop offset="40%" stopColor="#cbd5e1" />
          <stop offset="75%" stopColor="#64748b" />
          <stop offset="100%" stopColor="#1e293b" />
        </linearGradient>

        <filter id={`pipeGlow-${uid}`} x="-20%" y="-20%" width="140%" height="140%">
          <feDropShadow dx="0" dy="2" stdDeviation="2" floodColor="#000" floodOpacity="0.45" />
        </filter>

        <filter id={`elbowShadow-${uid}`} x="-30%" y="-30%" width="160%" height="160%">
          <feDropShadow dx="0" dy="2" stdDeviation="2.5" floodColor="#000" floodOpacity="0.5" />
        </filter>
      </defs>

      <style>{`
        @keyframes pipe-fluid-flow-${uid} {
          from { stroke-dashoffset: 40; }
          to { stroke-dashoffset: 0; }
        }
        .pipe-fluid-stream-${uid} {
          animation: pipe-fluid-flow-${uid} 0.8s linear infinite;
        }
      `}</style>

      {/* Layer 1: Ground Ambient Shadow */}
      <path
        d={pathData}
        fill="none"
        stroke="rgba(0, 0, 0, 0.45)"
        strokeWidth={diameter + 4}
        strokeLinecap="round"
        strokeLinejoin="round"
        filter={`url(#pipeGlow-${uid})`}
      />

      {/* Layer 2: Selection / Hover Glow Ring */}
      {(isSelected || isHovered) && (
        <path
          d={pathData}
          fill="none"
          stroke={isSelected ? 'var(--color-primary, #3b82f6)' : 'rgba(56, 189, 248, 0.6)'}
          strokeWidth={diameter + 6}
          strokeLinecap="round"
          strokeLinejoin="round"
          opacity={0.8}
        />
      )}

      {/* Layer 3: Outer Steel Pipe Wall (Dark Rim) */}
      <path
        d={pathData}
        fill="none"
        stroke="#0f172a"
        strokeWidth={diameter}
        strokeLinecap="round"
        strokeLinejoin="round"
      />

      {/* Layer 4: 3D Metallic Cylindrical Body */}
      <path
        d={pathData}
        fill="none"
        stroke={`url(#pipeGrad-${uid})`}
        strokeWidth={diameter - 2}
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeDasharray={isDraft ? '6,4' : undefined}
      />

      {/* Layer 5: Longitudinal Specular Highlight Sheen */}
      <path
        d={pathData}
        fill="none"
        stroke="rgba(255, 255, 255, 0.55)"
        strokeWidth={Math.max(1.5, diameter * 0.22)}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={isDraft ? 0.4 : 0.8}
      />

      {/* Layer 6: Dynamic Flowing Liquid Core */}
      {isFlowing && (
        <>
          {/* Static liquid fill core */}
          <path
            d={pathData}
            fill="none"
            stroke={liquidColor}
            strokeWidth={Math.max(2, diameter * 0.4)}
            strokeLinecap="round"
            strokeLinejoin="round"
            opacity={0.65}
          />
          {/* Flowing animated pulse dashes */}
          <path
            d={pathData}
            fill="none"
            stroke="#ffffff"
            strokeWidth={Math.max(1.5, diameter * 0.3)}
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeDasharray="10,12"
            className={`pipe-fluid-stream-${uid}`}
            opacity={0.85}
          />
        </>
      )}

      {/* Layer 7: REAL INDUSTRIAL ELBOW FITTINGS (Dirsek Parçaları) */}
      {elbows.map((elbow) => {
        const perp1 = { x: -elbow.inDir.y, y: elbow.inDir.x };
        const perp2 = { x: -elbow.outDir.y, y: elbow.outDir.x };

        // Entry collar endpoints
        const c1A = { x: elbow.entry.x - perp1.x * collarSpan, y: elbow.entry.y - perp1.y * collarSpan };
        const c1B = { x: elbow.entry.x + perp1.x * collarSpan, y: elbow.entry.y + perp1.y * collarSpan };

        // Exit collar endpoints
        const c2A = { x: elbow.exit.x - perp2.x * collarSpan, y: elbow.exit.y - perp2.y * collarSpan };
        const c2B = { x: elbow.exit.x + perp2.x * collarSpan, y: elbow.exit.y + perp2.y * collarSpan };

        // Elbow curved sleeve path
        const elbowArcD = `M ${elbow.entry.x} ${elbow.entry.y} Q ${elbow.x} ${elbow.y} ${elbow.exit.x} ${elbow.exit.y}`;

        // Apex midpoint for center reinforcement seam
        const midX = 0.25 * elbow.entry.x + 0.5 * elbow.x + 0.25 * elbow.exit.x;
        const midY = 0.25 * elbow.entry.y + 0.5 * elbow.y + 0.25 * elbow.exit.y;
        const chordDx = elbow.exit.x - elbow.entry.x;
        const chordDy = elbow.exit.y - elbow.entry.y;
        const chordLen = Math.hypot(chordDx, chordDy) || 1;
        const perpMid = { x: -chordDy / chordLen, y: chordDx / chordLen };
        const seamSpan = diameter * 0.58;
        const sA = { x: midX - perpMid.x * seamSpan, y: midY - perpMid.y * seamSpan };
        const sB = { x: midX + perpMid.x * seamSpan, y: midY + perpMid.y * seamSpan };

        return (
          <g key={elbow.id} className="industrial-pipe-elbow">
            {/* Elbow fitting shadow */}
            <path
              d={elbowArcD}
              fill="none"
              stroke="rgba(0, 0, 0, 0.4)"
              strokeWidth={diameter + 4}
              strokeLinecap="butt"
              filter={`url(#elbowShadow-${uid})`}
            />

            {/* Selection highlight for elbow fitting */}
            {(isSelected || isHovered) && (
              <path
                d={elbowArcD}
                fill="none"
                stroke={isSelected ? 'var(--color-primary, #3b82f6)' : 'rgba(56, 189, 248, 0.6)'}
                strokeWidth={diameter + 6}
                strokeLinecap="square"
                opacity={0.8}
              />
            )}

            {/* Elbow Outer Dark Casing (slightly thicker than pipe to show it's a fitting sleeve) */}
            <path
              d={elbowArcD}
              fill="none"
              stroke="#0f172a"
              strokeWidth={diameter + 2}
              strokeLinecap="butt"
            />

            {/* Elbow 3D Metallic Cast Body */}
            <path
              d={elbowArcD}
              fill="none"
              stroke={`url(#elbowGrad-${uid})`}
              strokeWidth={diameter + 0.6}
              strokeLinecap="butt"
            />

            {/* Elbow Casting Center Reinforcement Seam / Flange Mark */}
            <line
              x1={sA.x}
              y1={sA.y}
              x2={sB.x}
              y2={sB.y}
              stroke="#1e293b"
              strokeWidth={1.8}
              strokeLinecap="round"
              opacity={0.65}
            />
            <line
              x1={sA.x}
              y1={sA.y}
              x2={sB.x}
              y2={sB.y}
              stroke="#cbd5e1"
              strokeWidth={0.8}
              strokeLinecap="round"
              opacity={0.7}
            />

            {/* Elbow Specular Highlight Arc */}
            <path
              d={elbowArcD}
              fill="none"
              stroke="rgba(255, 255, 255, 0.75)"
              strokeWidth={Math.max(1.4, diameter * 0.22)}
              strokeLinecap="round"
              opacity={0.85}
            />

            {/* --- WELD COLLAR / FLANGE 1 (Entry Joint) --- */}
            {/* Outer Rim */}
            <line
              x1={c1A.x}
              y1={c1A.y}
              x2={c1B.x}
              y2={c1B.y}
              stroke="#0f172a"
              strokeWidth={3.2}
              strokeLinecap="round"
            />
            {/* Metallic Collar Ring */}
            <line
              x1={c1A.x}
              y1={c1A.y}
              x2={c1B.x}
              y2={c1B.y}
              stroke={`url(#collarGrad-${uid})`}
              strokeWidth={2.2}
              strokeLinecap="round"
            />
            {/* Collar Highlight */}
            <line
              x1={elbow.entry.x - perp1.x * (collarSpan * 0.6)}
              y1={elbow.entry.y - perp1.y * (collarSpan * 0.6)}
              x2={elbow.entry.x + perp1.x * (collarSpan * 0.6)}
              y2={elbow.entry.y + perp1.y * (collarSpan * 0.6)}
              stroke="#ffffff"
              strokeWidth={0.9}
              strokeLinecap="round"
              opacity={0.8}
            />

            {/* --- WELD COLLAR / FLANGE 2 (Exit Joint) --- */}
            {/* Outer Rim */}
            <line
              x1={c2A.x}
              y1={c2A.y}
              x2={c2B.x}
              y2={c2B.y}
              stroke="#0f172a"
              strokeWidth={3.2}
              strokeLinecap="round"
            />
            {/* Metallic Collar Ring */}
            <line
              x1={c2A.x}
              y1={c2A.y}
              x2={c2B.x}
              y2={c2B.y}
              stroke={`url(#collarGrad-${uid})`}
              strokeWidth={2.2}
              strokeLinecap="round"
            />
            {/* Collar Highlight */}
            <line
              x1={elbow.exit.x - perp2.x * (collarSpan * 0.6)}
              y1={elbow.exit.y - perp2.y * (collarSpan * 0.6)}
              x2={elbow.exit.x + perp2.x * (collarSpan * 0.6)}
              y2={elbow.exit.y + perp2.y * (collarSpan * 0.6)}
              stroke="#ffffff"
              strokeWidth={0.9}
              strokeLinecap="round"
              opacity={0.8}
            />

            {/* Small Weld Bead Dots at collar ends for hyper-realistic look */}
            <circle cx={c1A.x} cy={c1A.y} r={1.2} fill="#475569" stroke="#0f172a" strokeWidth={0.4} />
            <circle cx={c1B.x} cy={c1B.y} r={1.2} fill="#475569" stroke="#0f172a" strokeWidth={0.4} />
            <circle cx={c2A.x} cy={c2A.y} r={1.2} fill="#475569" stroke="#0f172a" strokeWidth={0.4} />
            <circle cx={c2B.x} cy={c2B.y} r={1.2} fill="#475569" stroke="#0f172a" strokeWidth={0.4} />
          </g>
        );
      })}

      {/* Selection Delete Handle */}
      {isSelected && onDelete && (
        <g
          onClick={(e) => {
            e.stopPropagation();
            onDelete();
          }}
          style={{ cursor: 'pointer' }}
        >
          {/* Helper delete button indicator rendered in SceneView or overlay */}
        </g>
      )}
    </g>
  );
};

